// Quelle: /opt/lilasprojekte/frontend/src/lib/theme.ts (Stand 2026-09-29)
// Geändert: Schlüssel hs.theme (Kommentar), Kartenabsatz gestrichen.
/* ============================================================
   Thema lesen, setzen, umschalten.

   Wie /opt/fragenbuero/frontend/src/lib/theme.ts, mit EINER Festlegung:
   DUNKEL ist die Vorgabe (E14) — und zwar hart, nicht über das System. Wer
   lieber hell arbeitet, schaltet einmal um, und die Wahl bleibt. Scans
   bleiben in beiden Themen lesbar: ein Ausschnitt liegt immer auf der
   Papierplatte (--papier) und wird nie eingefärbt.

   Der Schlüssel `hs.theme` muss mit public/theme.js übereinstimmen — dort
   steht derselbe Name, damit das Thema schon VOR dem ersten Anstrich sitzt
   und beim Laden nichts aufblitzt.
   ============================================================ */
import { create } from "zustand";
import { load, save } from "./storage";

export type Theme = "dark" | "light";

/** Schlüssel in localStorage: `hs.theme`. MUSS mit public/theme.js übereinstimmen. */
export const THEME_KEY = "theme";

export const THEME_STANDARD: Theme = "dark";

/** Die zwei Werte, die auch in public/theme.js und index.html stehen. */
const THEME_COLOR: Record<Theme, string> = { dark: "#070707", light: "#faf7f5" };

export function systemTheme(): Theme {
  try {
    return window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark";
  } catch {
    return THEME_STANDARD;
  }
}

export function readTheme(): Theme {
  const t = load<Theme | null>(THEME_KEY, null);
  if (t === "light" || t === "dark") return t;
  return THEME_STANDARD;
}

/** Setzt das Thema am Dokument — und NUR dort; gespeichert wird in setTheme(). */
export function applyTheme(theme: Theme): void {
  const el = document.documentElement;
  el.classList.toggle("dark", theme === "dark");
  el.classList.toggle("light", theme !== "dark");
  // Literale und nicht getComputedStyle: das läse hier noch den alten Wert.
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", THEME_COLOR[theme]);
}

/* ------------------------------------------------------------------ Speicher
   Ein winziger zustand-Speicher über denselben Werten. Grund: das Thema
   entscheidet nicht nur über eine CSS-Klasse, sondern über die DIAGRAMM-
   FARBEN — Canvas liest keine CSS-Variablen (CHART_COLORS[thema]). Ein Wert,
   der nur am <html>-Element hängt, löst kein Neuzeichnen aus; mit dem
   Speicher hängen Kopfleiste und Diagramme an derselben Quelle.

   `applyTheme` bleibt die einzige Stelle, die das Dokument anfasst. */

interface ThemaZustand {
  thema: Theme;
  setzen(t: Theme): void;
  umschalten(): void;
}

const useThemaStore = create<ThemaZustand>((set, get) => ({
  thema: readTheme(),
  setzen(t) {
    applyTheme(t);
    save(THEME_KEY, t);
    set({ thema: t });
  },
  umschalten() {
    get().setzen(get().thema === "dark" ? "light" : "dark");
  },
}));

export function setTheme(theme: Theme): void {
  useThemaStore.getState().setzen(theme);
}

export function toggleTheme(): Theme {
  useThemaStore.getState().umschalten();
  return useThemaStore.getState().thema;
}

/**
 * Der Hook für Kopfleiste und Diagramme:
 *   const { thema, setzen, umschalten } = useTheme();
 * `thema` ist "dark" oder "light" und ändert sich bei jedem Umschalten —
 * die Diagramme zeichnen mit `setOption(…, {notMerge:true})` neu.
 */
export function useTheme(): ThemaZustand {
  return useThemaStore();
}

/** Das Thema außerhalb von React (Diagrammaufbau vor dem ersten Anstrich). */
export function aktuellesThema(): Theme {
  return useThemaStore.getState().thema;
}
